import React, { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'
import { useAutoSave } from '../hooks/useAutoSave.js'

const GET_MANAGER_NOTES = `
  query GetManagerNotes($reportId: ID!, $sessionId: ID!) {
    getManagerNotes(reportId: $reportId, sessionId: $sessionId)
  }
`

const UPDATE_MANAGER_NOTES = `
  mutation UpdateManagerNotes($reportId: ID!, $sessionId: ID!, $notes: String!) {
    updateManagerNotes(reportId: $reportId, sessionId: $sessionId, notes: $notes)
  }
`

export function ManagerNotesPanel({ reportId, sessionId }) {
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!reportId || !sessionId) return
    setLoading(true)
    appsyncQuery(GET_MANAGER_NOTES, { reportId, sessionId })
      .then((data) => setNotes(data.getManagerNotes || ''))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [reportId, sessionId])

  const handleSave = useCallback(async (id, data) => {
    await appsyncQuery(UPDATE_MANAGER_NOTES, { reportId, sessionId, notes: data.notes })
  }, [reportId, sessionId])

  const saveState = useAutoSave(
    sessionId,
    { notes },
    handleSave,
    !loading
  )

  return (
    <div style={styles.panel} data-testid="manager-notes-panel">
      <div style={styles.header}>
        <span style={styles.label}>Your notes</span>
        {saveState === 'saving' && <span style={styles.saveIndicator}>Saving...</span>}
        {saveState === 'saved' && <span style={{ ...styles.saveIndicator, color: 'var(--energy-5)' }}>Saved</span>}
      </div>
      <p style={styles.hint}>Only you can see these notes. Your report cannot read them.</p>
      <textarea
        style={styles.textarea}
        value={loading ? '' : notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes about this session…"
        disabled={loading}
        rows={5}
        aria-label="Your notes"
      />
    </div>
  )
}

const styles = {
  panel: {
    borderTop: '1px solid var(--border)',
    paddingTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  hint: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    margin: '0 0 4px 0',
    lineHeight: 1.5,
  },
  saveIndicator: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '13px',
    lineHeight: '1.5',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    background: 'var(--bg)',
    color: 'var(--text-primary)',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
}
